/** Human labels for template field output kinds. */
export const FIELD_KIND_LABELS = {
  text: 'Short text',
  rich: 'Paragraphs',
  list: 'Bulleted list',
  table_rows: 'Table rows',
};

/** Human labels for template field data sources. */
export const FIELD_SOURCE_LABELS = {
  any: 'Any source',
  requirements: 'Requirements',
  sessions: 'Sessions',
  project: 'Project info',
};

/** Instruction verbs that are never nouns: dropped when followed by their object. */
const VERBS = 'write|describe|summari[sz]e|generate|create|provide|insert|add|enter|give|explain|include|draft|identify|define|capture|specify|fill in|put|elaborate on|break down|lay out';
/** Words that are verbs or nouns ("List the risks" vs "List of risks"): dropped only before an article or determiner. */
const AMBIGUOUS_VERBS = 'list|outline|state|name|document|detail|record|note|show|present|highlight|report';
const DETERMINERS = 'the|a|an|all|any|each|every|our|your|their|its|this|these|those|key|main|some|up to|\\d+';
const LEADING_VERB = new RegExp(
  `^(?:please\\s+)?(?:(?:${VERBS})\\b(?!\\s+(?:of|and|or|&)\\b)|(?:${AMBIGUOUS_VERBS})\\b(?=\\s+(?:${DETERMINERS})\\b))\\s*`,
  'i'
);
const LEADING_ARTICLE = /^(?:(?:a|an|the)\s+)+/i;
/** Leading qualifiers such as "(Optional)" or "[Required]". */
const LEADING_QUALIFIER = /^(?:[([][^)\]]*[)\]]\s*)+/;
/**
 * Where an instruction stops naming its subject and starts adding detail. A period is handled
 * separately (see findDetailBreak) so abbreviations such as "e.g." do not end the summary.
 */
const DETAIL_BREAK = /\s*(?:[,;:!?()[\]{}—–]|\s-\s|\b(?:based on|using|from|for|that|which|with|including|in \d|in order|of no more|in (?:a|an|the|one|two|three|\d+)\b)\b)/i;
/** A sentence-ending period: followed by whitespace and a capital letter, or the end of the text. */
const SENTENCE_PERIOD = /\.(?=\s+[A-Z]|\s*$)/g;
/** Short abbreviations whose period never ends a sentence. */
const ABBREVIATION = /(?:^|[\s(])(?:e\.g|i\.e|etc|vs|approx|incl|esp|no|nr|fig|cf|[A-Za-z])$/i;
/** A summary must not start with a connector left over from a stripped word. */
const DANGLING_START = /^(?:of|and|or|to|for|with|by|in|on|at|&)\b/i;
/** Leading punctuation or dashes left after stripping the verb ("Write, in one line, …"). */
const LEADING_PUNCTUATION = /^[\s,.;:!?—–-]+/;
/** Labels too generic to tell headings apart on their own ("Title", "Heading"). */
const GENERIC_LABEL = /^(?:title|heading|headline|name|label|section|summary|text|content|paragraph)$/i;
const HAS_WORD = /[\p{L}\p{N}]/u;
/** A leading aside before the subject ("in one line, …", "briefly — …"). */
const LEADING_ASIDE = /^(?:(?:(?:in|using|with|for|from|based on)\b[^,—–]*|briefly|concisely|clearly|shortly)\s*[,—–]\s*|in (?:\d+(?:-\d+)?|one|two|three|four|five|a|an|a few) (?:short )?(?:sentences?|paragraphs?|lines?|words?|bullets?|bullet points?|points?)\b\s*)/i;

/**
 * Index of the first sentence end in `text`, skipping abbreviations (-1 when none).
 * @param {string} text - Text.
 * @returns {number} Index of the period.
 */
const findSentenceEnd = (text) => {
  SENTENCE_PERIOD.lastIndex = 0;
  let match = SENTENCE_PERIOD.exec(text);
  while (match) {
    if (!ABBREVIATION.test(text.slice(0, match.index))) return match.index;
    match = SENTENCE_PERIOD.exec(text);
  }
  return -1;
};

/**
 * Index of the first detail break in `text` (-1 when none).
 * @param {string} text - Text.
 * @returns {number} Break index.
 */
const findDetailBreak = (text) => {
  const candidates = [text.search(DETAIL_BREAK), findSentenceEnd(text)].filter((i) => i >= 0);
  return candidates.length ? Math.min(...candidates) : -1;
};

/**
 * Text up to its first sentence end.
 * @param {string} text - Text.
 * @returns {string} First sentence.
 */
const firstSentence = (text) => {
  const end = findSentenceEnd(text);
  return end > 0 ? text.slice(0, end) : text;
};

/**
 * Cap text at `maxWords`, dropping trailing punctuation.
 * @param {string} text - Text.
 * @param {number} maxWords - Word cap.
 * @returns {string} Capped text.
 */
const capWords = (text, maxWords) => {
  const words = text.trim().split(' ').filter(Boolean);
  const head = words.slice(0, maxWords).join(' ').replace(/[\s,.;:!?—–-]+$/, '');
  return head + (words.length > maxWords ? '…' : '');
};

/**
 * Cut an instruction at its first detail clause and cap it at `maxWords`.
 * @param {string} text - Text.
 * @param {number} maxWords - Word cap.
 * @returns {string} Trimmed text.
 */
const clip = (text, maxWords) => {
  const breakAt = findDetailBreak(text);
  return capWords(breakAt > 0 ? text.slice(0, breakAt) : text, maxWords);
};

const wordCount = (text) => text.replace(/…$/, '').split(' ').filter(Boolean).length;

/**
 * Short, readable summary of an AI field instruction for places that name the field without
 * showing it (the outline shows AI-generated headings this way): drops a leading qualifier,
 * instruction verb, article and stray punctuation, stops at the first clause that adds detail,
 * and keeps at most `maxWords`. Nouns that look like verbs are kept ("Name of the customer"
 * stays as it is). When the cut leaves a single or generic word ("Title", "Write"), the summary
 * runs past the break so headings stay distinguishable ("Title for this section").
 * "Describe the current challenges the customer faces, based on…" -> "Current challenges the customer faces".
 * @param {string} instruction - Field instruction.
 * @param {number} [maxWords] - Word cap.
 * @returns {string} Summary ('' when the instruction is empty).
 */
export function summarizeFieldInstruction(instruction, maxWords = 5) {
  const original = String(instruction || '').replace(/\s+/g, ' ').trim();
  if (!original) return '';
  const unqualified = original.replace(LEADING_QUALIFIER, '').replace(LEADING_PUNCTUATION, '').trim() || original;
  let stripped = unqualified.replace(LEADING_VERB, '').replace(LEADING_PUNCTUATION, '');
  for (let i = 0; i < 3 && LEADING_ASIDE.test(stripped); i += 1) stripped = stripped.replace(LEADING_ASIDE, '');
  stripped = stripped.replace(LEADING_ARTICLE, '').replace(LEADING_PUNCTUATION, '');
  // Fall back to the instruction itself when stripping left nothing readable.
  const usable = HAS_WORD.test(stripped) && !DANGLING_START.test(stripped);
  const source = usable ? stripped : unqualified;
  let summary = clip(source, maxWords);
  if (!HAS_WORD.test(summary) || !/^[\p{L}\p{N}]/u.test(summary)) summary = capWords(firstSentence(source.replace(/^[^\p{L}\p{N}]+/u, '')), maxWords);
  // Too generic or a single word: keep going past the detail break (within the first sentence).
  const sentence = firstSentence(source);
  if ((wordCount(summary) <= 1 || GENERIC_LABEL.test(summary)) && wordCount(sentence) > 1) {
    summary = capWords(sentence, maxWords);
  }
  if (!HAS_WORD.test(summary)) summary = unqualified;
  return summary.charAt(0).toUpperCase() + summary.slice(1);
}
